/**
 * Pure acca helpers shared by the Acca tab (AccaBuilder) and the Dashboard tab
 * badge, so the two can never disagree about which legs exist or are open.
 *
 * Leg status:
 *   open     - not kicked off (no result, SCHEDULED / NOT_STARTED); selectable
 *   started  - kicked off (per kickoff time) but the results feed has no
 *              live/final status yet (the FootyStats "incomplete" gap, or a
 *              league live scores don't cover); not selectable, not counted
 *              as open
 *   live     - in play / half time
 *   void     - postponed / suspended / cancelled (dropped from combined odds)
 *   won/lost - finished, settled with the same rules as PredictionCard's checkWin
 *   finished - finished but the feed has no score yet
 */

import { parseKickoffTime } from './resultsPolling';

// Goals legs (btts+over25) mix on one slip; draw and outsiders are separate slips.
export const SYSTEM_OF = { btts: 'goals', over25: 'goals', draw: 'draw', outcome: 'outsiders' };
export const SYSTEM_ORDER = ['goals', 'draw', 'outsiders'];

const ODDS_FIELD = { btts: 'odds_btts', over25: 'odds_over25', draw: 'odds_draw', outcome: 'odds' };
const MARKETS = ['btts', 'over25', 'draw', 'outcome'];

const OPEN_STATUSES = new Set(['SCHEDULED', 'NOT_STARTED', 'TIMED']);
const LIVE_STATUSES = new Set(['IN_PLAY', 'HALF_TIME', 'PAUSED', 'HALFTIME', 'LIVE']);
const VOID_STATUSES = new Set(['POSTPONED', 'SUSPENDED', 'CANCELLED', 'CANCELED', 'ABANDONED']);

export const ACCA_SELECTED_KEY = 'acca_selected_v1';

export function legKey(leg) {
  return `${leg.market}_${leg.id}`;
}

/**
 * The result to judge a prediction by. Mirrors Dashboard's predictionsWithResults:
 * the live/API result wins when it carries a meaningful (non-SCHEDULED) status,
 * otherwise the result embedded in the prediction, otherwise the API row.
 */
function resolveResult(prediction, results) {
  const api = prediction && prediction.id != null ? results?.[prediction.id] : null;
  if (api && api.status && api.status !== 'SCHEDULED') return api;
  return prediction?.result || api || null;
}

/** Same per-market rules as PredictionCard's checkWin. */
function settle(market, prediction, homeGoals, awayGoals) {
  if (market === 'btts') return homeGoals > 0 && awayGoals > 0;
  if (market === 'over25') return homeGoals + awayGoals >= 3;
  if (market === 'draw') return homeGoals === awayGoals;
  const actual = homeGoals > awayGoals ? 'H' : homeGoals < awayGoals ? 'A' : 'D';
  return prediction?.prediction === actual;
}

function statusFor(market, prediction, result, kickoffMs, now) {
  if (prediction?.status === 'POSTPONED') return 'void';
  const s = result?.status;
  if (!s || OPEN_STATUSES.has(s)) {
    // Kicked off (by kickoff time) but the feed still has no live/final status:
    // the results cron maps FootyStats "incomplete" (including in-progress
    // games) to SCHEDULED, and live scores don't cover every league.
    if (kickoffMs != null && now != null && kickoffMs <= now) return 'started';
    return 'open';
  }
  if (VOID_STATUSES.has(s)) return 'void';
  if (LIVE_STATUSES.has(s)) return 'live';
  if (s === 'FINISHED') {
    if (result.homeGoals == null || result.awayGoals == null) return 'finished';
    return settle(market, prediction, result.homeGoals, result.awayGoals) ? 'won' : 'lost';
  }
  return 'open';
}

function scoreOf(result, status) {
  if (!result || status === 'open' || status === 'void') return null;
  return {
    status: result.status,
    homeGoals: result.homeGoals ?? null,
    awayGoals: result.awayGoals ?? null,
    elapsedMinutes: result.elapsedMinutes ?? null,
    matchTime: result.matchTime ?? null,
  };
}

/** Every acca_candidate leg across all systems, with status + result. */
export function buildAccaLegs(predictions, results, now = Date.now()) {
  const legs = [];
  for (const market of MARKETS) {
    for (const p of predictions?.[market] || []) {
      if (p.acca_candidate !== true) continue;
      const result = resolveResult(p, results);
      const kickoffMs = parseKickoffTime(p.date_time);
      const status = statusFor(market, p, result, kickoffMs, now);
      legs.push({
        key: legKey({ market, id: p.id }),
        id: p.id,
        market,
        system: SYSTEM_OF[market],
        home_team: p.home_team,
        away_team: p.away_team,
        league: p.league,
        date_time: p.date_time,
        prediction: p.prediction,
        bet_label: market === 'outcome'
          ? (p.prediction === 'H' ? p.home_team : p.prediction === 'A' ? p.away_team : p.prediction_label || p.prediction)
          : undefined,
        odds: p[ODDS_FIELD[market]],
        composite_grade: p.composite_grade || 0,
        grade_letter: p.grade_letter || null,
        status,
        result: scoreOf(result, status),
      });
    }
  }
  return legs.sort((a, b) =>
    (SYSTEM_ORDER.indexOf(a.system) - SYSTEM_ORDER.indexOf(b.system))
    || (b.composite_grade - a.composite_grade));
}

export function countOpenAccaLegs(predictions, results, now = Date.now()) {
  return buildAccaLegs(predictions, results, now).filter(l => l.status === 'open').length;
}

/** Tab badge: the user's selected leg count when > 0, else open candidate legs. */
export function accaBadgeCount(predictions, results, selected, now = Date.now()) {
  const legs = buildAccaLegs(predictions, results, now);
  const selectedCount = legs.filter(l => selected?.[l.key]).length;
  return selectedCount > 0 ? selectedCount : legs.filter(l => l.status === 'open').length;
}

/** Drop selections whose leg is no longer in the batch. Same object if unchanged. */
export function pruneSelected(selected, legs) {
  if (!selected) return {};
  const valid = new Set(legs.map(l => l.key));
  const out = {};
  let changed = false;
  for (const [k, v] of Object.entries(selected)) {
    if (v && valid.has(k)) out[k] = true;
    else changed = true;
  }
  return changed ? out : selected;
}

/** Combined odds over non-void legs (won/lost/open/live all count). */
export function slipSummary(selectedLegs) {
  const activeLegs = selectedLegs.filter(l => l.status !== 'void');
  return {
    activeLegs,
    voidCount: selectedLegs.length - activeLegs.length,
    combinedOdds: activeLegs.reduce((acc, l) => acc * (l.odds || 1), 1),
  };
}

const normName = s => String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');

function findTicketPrediction(leg, predictions) {
  if (leg.id != null) {
    for (const m of [leg.market, ...Object.keys(predictions || {})]) {
      const list = predictions?.[m];
      if (!Array.isArray(list)) continue;
      const hit = list.find(p => p && p.id === leg.id);
      if (hit) return hit;
    }
    return null;
  }
  // Tickets published before legs carried ids: match on team names.
  const h = normName(leg.home);
  const a = normName(leg.away);
  const same = p => p && normName(p.home_team) === h && normName(p.away_team) === a;
  const own = (predictions?.[leg.market] || []).find(same);
  if (own) return own;
  for (const list of Object.values(predictions || {})) {
    if (!Array.isArray(list)) continue;
    const hit = list.find(same);
    if (hit) return hit;
  }
  return null;
}

/** Suggested-ticket legs matched to results (by id, else by team names). */
export function matchTicketLegs(ticket, predictions, results) {
  if (!ticket || !Array.isArray(ticket.legs)) return [];
  return ticket.legs.map(l => {
    const pred = findTicketPrediction(l, predictions);
    const id = l.id ?? pred?.id ?? null;
    const result = pred
      ? resolveResult(pred, results)
      : (id != null ? results?.[id] || null : null);
    // Settle on the ticket's market; the prediction only lends id/result, but an
    // outcome leg still needs a prediction (H/A/D) to settle against, so use the
    // ticket leg's own `prediction` when there's no matched prediction to borrow it from.
    const status = statusFor(l.market, { prediction: l.prediction ?? pred?.prediction }, result);
    return {
      ...l,
      id,
      home_team: l.home,
      away_team: l.away,
      date_time: l.date || pred?.date_time,
      status,
      result: scoreOf(result, status),
    };
  });
}

/** Overall ticket state: lost on any loss, won once every non-void leg won. */
export function ticketSummary(legs) {
  const count = s => legs.filter(l => l.status === s).length;
  const won = count('won');
  const lost = count('lost');
  const voided = count('void');
  const total = legs.length - voided;
  const settled = won + lost;
  const state = lost > 0 ? 'lost' : total > 0 && won === total ? 'won' : 'pending';
  return { won, lost, void: voided, total, settled, state };
}

export function loadAccaSelected() {
  try {
    const raw = window.localStorage.getItem(ACCA_SELECTED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function saveAccaSelected(selected) {
  try {
    window.localStorage.setItem(ACCA_SELECTED_KEY, JSON.stringify(selected || {}));
  } catch {
    // storage blocked / full / private mode: selection just won't persist
  }
}
